import type { ChartDataPoint, ChartSeries } from '@/types/data';

const DEFAULT_AXIS_WIDTH = 48;
const DEFAULT_AXIS_MAX_WIDTH = 112;
const DEFAULT_AXIS_TICK_MARGIN = 8;
const DEFAULT_AXIS_PADDING = 12;
const DEFAULT_AXIS_CHARACTER_WIDTH = 7;

/** Sanitize series id for use as CSS variable and Recharts data keys. */
function sanitizeChartKey(id: string | undefined | null): string {
  if (!id) return '';
  return id.replace(/\s+/g, '_');
}

function getFiniteChartValue(value: number | undefined, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function getNonNegativeChartValue(
  value: number | undefined,
  fallback = 0,
): number {
  return Math.max(0, getFiniteChartValue(value, fallback));
}

function getFinitePositiveChartValue(
  value: number | undefined,
  fallback: number,
): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? value
    : fallback;
}

function clampChartPercent(value: number | undefined): number {
  return Math.min(100, getNonNegativeChartValue(value));
}

function getZeroBasedChartDomain(values: number[]): [number, number] {
  const finiteValues = values.filter(Number.isFinite);
  if (finiteValues.length === 0) {
    return [0, 0];
  }

  return [0, Math.max(0, ...finiteValues)];
}

function getFiniteChartDomain(
  domain: [number, number] | undefined,
): [number, number] | undefined {
  if (!domain) {
    return undefined;
  }

  const [min, max] = domain;
  return Number.isFinite(min) && Number.isFinite(max) ? [min, max] : undefined;
}

function toSeriesChartData(
  data: ChartDataPoint[],
  series: ChartSeries[],
): Record<string, string | number>[] {
  return data.map((dataPoint) => {
    const point: Record<string, string | number> = {
      label: dataPoint.label,
    };
    for (const chartSeries of series) {
      point[sanitizeChartKey(chartSeries.id)] = getFiniteChartValue(
        dataPoint.values[chartSeries.id],
      );
    }
    return point;
  });
}

interface EstimateChartAxisWidthOptions {
  data?: ChartDataPoint[];
  series?: ChartSeries[];
  values?: unknown[];
  labels?: unknown[];
  ticks?: number[];
  formatter?: (value: number) => string;
  includeSeriesTotals?: boolean;
  minWidth?: number;
  maxWidth?: number;
  tickMargin?: number;
  padding?: number;
  characterWidth?: number;
}

function collectFiniteAxisValues(values: unknown[] | undefined): number[] {
  if (!values) {
    return [];
  }

  return values.filter(
    (value): value is number =>
      typeof value === 'number' && Number.isFinite(value),
  );
}

function collectSeriesAxisValues({
  data,
  series,
  includeSeriesTotals,
}: Pick<
  EstimateChartAxisWidthOptions,
  'data' | 'series' | 'includeSeriesTotals'
>): number[] {
  if (!data || !series) {
    return [];
  }

  const values: number[] = [];
  for (const dataPoint of data) {
    let positiveTotal = 0;
    let negativeTotal = 0;
    let hasFiniteSeriesValue = false;

    for (const chartSeries of series) {
      const value = dataPoint.values[chartSeries.id];
      if (!Number.isFinite(value)) {
        continue;
      }

      values.push(value);
      hasFiniteSeriesValue = true;

      if (value >= 0) {
        positiveTotal += value;
      } else {
        negativeTotal += value;
      }
    }

    if (includeSeriesTotals && hasFiniteSeriesValue) {
      values.push(positiveTotal);
      if (negativeTotal !== 0) {
        values.push(negativeTotal);
      }
    }
  }

  return values;
}

function formatAxisValue(
  value: number,
  formatter: ((value: number) => string) | undefined,
): string {
  return formatter ? formatter(value) : value.toLocaleString();
}

function normalizeAxisLabels(labels: unknown[] | undefined): string[] {
  if (!labels) {
    return [];
  }

  return labels
    .filter((label) => label !== null && label !== undefined)
    .map((label) => String(label))
    .filter(Boolean);
}

function estimateChartAxisWidth({
  data,
  series,
  values,
  labels,
  ticks,
  formatter,
  includeSeriesTotals = false,
  minWidth = DEFAULT_AXIS_WIDTH,
  maxWidth = DEFAULT_AXIS_MAX_WIDTH,
  tickMargin = DEFAULT_AXIS_TICK_MARGIN,
  padding = DEFAULT_AXIS_PADDING,
  characterWidth = DEFAULT_AXIS_CHARACTER_WIDTH,
}: EstimateChartAxisWidthOptions): number {
  const resolvedMinWidth = getFinitePositiveChartValue(
    minWidth,
    DEFAULT_AXIS_WIDTH,
  );
  const resolvedMaxWidth = Math.max(
    resolvedMinWidth,
    getFinitePositiveChartValue(maxWidth, DEFAULT_AXIS_MAX_WIDTH),
  );
  const resolvedTickMargin = getNonNegativeChartValue(
    tickMargin,
    DEFAULT_AXIS_TICK_MARGIN,
  );
  const resolvedPadding = getNonNegativeChartValue(
    padding,
    DEFAULT_AXIS_PADDING,
  );
  const resolvedCharacterWidth = getFinitePositiveChartValue(
    characterWidth,
    DEFAULT_AXIS_CHARACTER_WIDTH,
  );

  const numericValues =
    ticks && ticks.length > 0
      ? collectFiniteAxisValues(ticks)
      : [
          ...collectSeriesAxisValues({ data, series, includeSeriesTotals }),
          ...collectFiniteAxisValues(values),
        ];
  const measuredLabels = [
    ...normalizeAxisLabels(labels),
    ...numericValues.map((value) => formatAxisValue(value, formatter)),
  ];

  if (measuredLabels.length === 0) {
    return resolvedMinWidth;
  }

  const widestLabelLength = Math.max(
    ...measuredLabels.map((label) => label.length),
  );
  const estimatedWidth = Math.ceil(
    widestLabelLength * resolvedCharacterWidth +
      resolvedTickMargin +
      resolvedPadding,
  );

  return Math.min(resolvedMaxWidth, Math.max(resolvedMinWidth, estimatedWidth));
}

export {
  clampChartPercent,
  estimateChartAxisWidth,
  getFiniteChartDomain,
  getFiniteChartValue,
  getFinitePositiveChartValue,
  getNonNegativeChartValue,
  getZeroBasedChartDomain,
  sanitizeChartKey,
  toSeriesChartData,
};
